/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { FC, useContext } from "react";

import { Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { formatNarrowCurrencyAmount, getDollarValue } from "@simplyk/common";

import { FrontendFormContext } from "../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../common/contexts/LocaleContext";
import { trpc } from "../../../common/helpers/trpc";

import { ThermometerProps } from "./types";
import { useSpreadRefreshInterval } from "./useSpreadRefreshInterval";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const Thermometer: FC<ThermometerProps> = ({
  max,
  form,
  volume,
  refreshIntervalMs,
  gutterBottom,
  className,
}) => {
  const { t } = useTranslate();

  const { currency } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();
  const isVolumeProvided = volume !== undefined;
  const refetchInterval = useSpreadRefreshInterval(refreshIntervalMs);
  const { data: progressValue, isLoading: loading } = trpc.form_getStoredFormVolume.useQuery(
    {
      formId: form.id,
      formType: form.formType,
    },
    {
      enabled: !isVolumeProvided || refetchInterval !== false,
      initialData: volume,
      refetchOnMount: !isVolumeProvided,
      refetchOnReconnect: !isVolumeProvided,
      refetchOnWindowFocus: !isVolumeProvided,
      refetchInterval,
    }
  );
  const volumeValue = progressValue ?? 0;

  if (!isVolumeProvided && loading) {
    return <Skeleton variant="rectangular" height={44} animation="wave" />;
  }

  return (
    <Box
      className={className as unknown as string}
      sx={(theme) => ({
        width: "100%",
        ...(gutterBottom && { marginBottom: theme.spacing(2) }),
      })}
    >
      {max && (
        <>
          <Box
            sx={(theme) => ({
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: theme.spacing(1),
              [theme.breakpoints.up("md")]: { marginTop: theme.spacing(1) },
            })}
          >
            <DashboardTypography variant="h6" data-test="thermometer-progress">
              {formatNarrowCurrencyAmount(getDollarValue(volumeValue), { locale: isoLocale, currency })}
            </DashboardTypography>
            <DashboardTypography variant="body1" display="inline" sx={(theme) => ({ marginLeft: theme.spacing(0.5) })}>
              {t("donationForm", "of")}{" "}
            </DashboardTypography>
            <DashboardTypography variant="body1" display="inline" sx={(theme) => ({ marginLeft: theme.spacing(0.5) })}>
              {formatNarrowCurrencyAmount(getDollarValue(max), { locale: isoLocale, currency })}
            </DashboardTypography>
          </Box>
          <LinearProgress
            color="primary"
            value={volumeValue > max ? 100 : (volumeValue / max) * 100}
            sx={{
              width: "100%",
              height: 12,
              borderRadius: "5px",
              border: "1px rgb(200,200,200) solid",
            }}
            variant="determinate"
          />
        </>
      )}
    </Box>
  );
};
